"use client";

import { useEffect, useState } from "react";
import type { Award, Sport } from "@/lib/conditions/awards-types";
import { useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import { AwardsList } from "./AwardsList";
import { Methodology } from "./Methodology";
import { isSupProfileKey } from "@/lib/conditions/sport-profiles";

// Renders the awards list and switches period client-side. The default
// ("all-time") awards are server-rendered as initialAwards. Legacy periods keep
// the historical API cache policy; named SUP periods are request-time and
// explicitly bypass the browser cache. The SeasonSelector writes ?period via
// the shallow-search-param store, which this component subscribes to.
export function AwardsBrowser({
  sport,
  initialAwards,
}: {
  sport: Sport;
  initialAwards: Award[];
}) {
  const period = useShallowSearchParam("period") ?? "all-time";
  const [remoteAwards, setRemoteAwards] = useState<Award[]>([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const awards = period === "all-time" ? initialAwards : remoteAwards;
  const loading = period !== "all-time" && remoteLoading;

  useEffect(() => {
    if (period === "all-time") {
      return;
    }
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) setRemoteLoading(true);
    });
    fetch(`/api/awards/${sport}?period=${encodeURIComponent(period)}`, {
      cache: isSupProfileKey(sport) ? "no-store" : "default",
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data: Award[]) => {
        if (cancelled) return;
        setRemoteAwards(Array.isArray(data) ? data : []);
        setRemoteLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setRemoteAwards([]);
        setRemoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period, sport]);

  return (
    <div
      className={`transition-opacity duration-200 ${loading ? "opacity-50" : "opacity-100"}`}
      aria-busy={loading}
    >
      <AwardsList awards={awards} />
      {awards.length > 0 && <Methodology awards={awards} />}
    </div>
  );
}
